import { FC, useMemo } from 'react';
import { Column, FuzzySearchSelect } from '@/components/UI/FuzzySearchSelect/FuzzySearchSelect';
import { useAddSamplePlanContext } from '@/components/Samples/AddSamplePlanProvider';
import { RiSquareFill } from 'react-icons/ri';
import { PlannedSampleOption, useAssignAnimalsContext } from '@/components/Samples/AssignAnimals/AssignAnimalsProvider';
import { _notNil } from '@/littledash';
import styles from './AssignAnimalsForm.module.scss';
import { GroupTarget } from '@/model/SamplePlan.model';

const searchIndex = [
  { name: 'name' },
  { name: ['plannedSamples', 'name'] },
  { name: ['plannedSamples', 'sample_name'] },
];
type ItemData = GroupTarget & { plannedSamples: Array<PlannedSampleOption> };

export const GroupSearchSelect: FC = () => {
  const { groups } = useAddSamplePlanContext();
  const { selectedGroupTargets, targetToPlannedSamples, plannedSampleOptions, updateSelection } =
    useAssignAnimalsContext();

  const items = useMemo<Map<GroupTarget['api_id'], ItemData>>(() => {
    return new Map(
      Array.from(groups.values()).map((groupTarget) => {
        const plannedSamples = Array.from(targetToPlannedSamples.get(groupTarget.api_id) ?? []).reduce<
          Array<PlannedSampleOption>
        >((acc, plannedSampleApiId) => {
          const plannedSample = plannedSampleOptions.get(plannedSampleApiId);
          if (_notNil(plannedSample)) {
            acc.push(plannedSample);
          }
          return acc;
        }, []);
        return [groupTarget.api_id, { ...groupTarget, plannedSamples }];
      })
    );
  }, [groups, targetToPlannedSamples, plannedSampleOptions]);

  const columns = useMemo<Array<Column<ItemData>>>(
    () => [
      {
        id: 'name',
        title: 'Groups',
        cell: (data) => (
          <div className="flex flex-row items-center gap-h-1">
            <RiSquareFill color={data.color} />
            <span>{data.name}</span>
          </div>
        ),
      },
      { id: 'max_subjects', title: 'No. Animals', cell: (data) => data.max_subjects },
      {
        id: 'planned_sample_group',
        title: 'Planned sample group',
        cell: (data) => (
          <div className={styles.Chips}>
            {data.plannedSamples.map((sample) => (
              <div className={styles.Chip} key={sample.api_id}>
                {sample.name}
              </div>
            ))}
          </div>
        ),
      },
    ],
    []
  );

  const selected = useMemo(() => {
    const result = new Map<GroupTarget['api_id'], boolean>();
    selectedGroupTargets.forEach((key) => {
      result.set(key, true);
    });
    return result;
  }, [selectedGroupTargets]);

  return (
    <FuzzySearchSelect
      columns={columns}
      items={items}
      searchIndex={searchIndex}
      selected={selected}
      testId="group-search-select"
      onSelectionChange={(value) => updateSelection({ type: 'target', targetType: 'groups', value })}
    />
  );
};
